import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Truck, ShoppingBag, Power, CheckCircle2 } from "lucide-react";

// mode: "close" = O que deseja fechar? | "open" = O que deseja abrir?
const CHANNEL_OPTIONS = [
  {
    id: "delivery",
    icon: Truck,
    close: { label: "Fechar apenas Delivery", description: "Bloqueia pedidos de entrega no site" },
    open:  { label: "Abrir apenas Delivery",  description: "Permite pedidos de entrega no site" },
  },
  {
    id: "pickup",
    icon: ShoppingBag,
    close: { label: "Fechar apenas Retirada / Pickup", description: "Bloqueia pedidos para retirada no local" },
    open:  { label: "Abrir apenas Retirada / Pickup",  description: "Permite pedidos para retirada no local" },
  },
  {
    id: "all",
    icon: Power,
    close: { label: "Fechar Restaurante inteiro", description: "Bloqueia todos os canais públicos (delivery + retirada)" },
    open:  { label: "Abrir Restaurante inteiro",  description: "Abre todos os canais públicos (delivery + retirada)" },
  },
];

export function ChannelModal({ open, onClose, mode, restaurant, onToggle }) {
  const [confirming, setConfirming] = useState(null);

  const handleClose = () => {
    setConfirming(null);
    onClose();
  };

  const handleConfirm = () => {
    onToggle(confirming, mode === "open");
    setConfirming(null);
    onClose();
  };

  // Filter options that are relevant to current mode
  const visibleOptions = CHANNEL_OPTIONS.filter(opt => {
    if (mode === "close") {
      if (opt.id === "delivery") return restaurant?.delivery_enabled !== false;
      if (opt.id === "pickup")   return restaurant?.pickup_enabled !== false;
    } else {
      if (opt.id === "delivery") return restaurant?.delivery_enabled === false;
      if (opt.id === "pickup")   return restaurant?.pickup_enabled === false;
    }
    return true; // "all" always visible
  });

  const selectedOpt = CHANNEL_OPTIONS.find(o => o.id === confirming);
  const isClose = mode === "close";
  const title = isClose ? "O que deseja fechar?" : "O que deseja abrir?";

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{confirming ? "Confirmar ação" : title}</DialogTitle>
        </DialogHeader>

        {!confirming ? (
          <div className="space-y-2 py-1">
            {isClose && (
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-700">
                  Atenção: esta ação pode impedir clientes de fazer pedidos no site.
                </p>
              </div>
            )}

            {visibleOptions.map(opt => {
              const Icon = opt.icon;
              const copy = isClose ? opt.close : opt.open;
              const isAll = opt.id === "all";
              const borderColor = isClose
                ? (isAll ? "border-red-200 hover:bg-red-50" : "border-orange-200 hover:bg-orange-50")
                : "border-emerald-200 hover:bg-emerald-50";
              const iconColor  = isClose
                ? (isAll ? "text-red-500" : "text-orange-500")
                : "text-emerald-600";
              const labelColor = isClose
                ? (isAll ? "text-red-700" : "text-orange-700")
                : "text-emerald-700";

              return (
                <button
                  key={opt.id}
                  onClick={() => setConfirming(opt.id)}
                  className={`w-full flex items-start gap-3 p-3 rounded-xl border text-left transition-colors ${borderColor}`}
                >
                  <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${iconColor}`} />
                  <div>
                    <p className={`text-sm font-semibold ${labelColor}`}>{copy.label}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{copy.description}</p>
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="space-y-4 py-2">
            {isClose && (
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-700">
                  Atenção: esta ação pode impedir clientes de fazer pedidos no site.
                </p>
              </div>
            )}
            {!isClose && (
              <div className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                <p className="text-xs text-emerald-700">
                  Os clientes poderão fazer pedidos novamente pelo site.
                </p>
              </div>
            )}
            <div>
              <p className="text-sm font-bold text-gray-800">
                {selectedOpt ? (isClose ? selectedOpt.close.label : selectedOpt.open.label) : ""}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {selectedOpt ? (isClose ? selectedOpt.close.description : selectedOpt.open.description) : ""}
              </p>
            </div>
            <p className="text-sm text-gray-600">Confirma esta ação?</p>
            <div className="flex gap-2 justify-end pt-1">
              <Button variant="outline" size="sm" onClick={() => setConfirming(null)}>Voltar</Button>
              <Button
                size="sm"
                className={isClose ? "bg-red-600 hover:bg-red-700" : "bg-emerald-600 hover:bg-emerald-700"}
                onClick={handleConfirm}
              >
                Confirmar
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
